import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { GridColDef, GridColumnVisibilityModel, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';
import LanguageUtils from '../../../utils/LanguageUtils';
import HelptextConstantsEnum from '../../../constants/helptextConstantsEnum';
import TableUtils from '../../../utils/tableUtils';
import DataGridComponent from '../../common/dataGrid';
import UrlConstants from '../../../constants/UrlConstants';
import CustomStringLink from '../../common/customStringLink';
import NullableFormattedDate from '../../common/nullableFormattedDate';
import TruncatedStringTooltip from '../../common/truncatedStringTooltip';

interface CustomerCancellationRuleSetListProps {
    classes?: any;
    checkSelection?: any;
    isLoading?: boolean;
    multipleSelection?: boolean;
    componentsProps?: any;
    onPageChange?: any;
    rows?: any;
    applyRowFn?: any;
    hiddenColumns?: Array<string>;
    onSortModelChange?: any;
    columnVisibilityModel?: GridColumnVisibilityModel;
    onColumnVisibilityModelChange?: any;
    setCurrentComponentId?: any;
    disableLinks?: boolean;
    isFromCancellationRuleSet?: boolean
}

const messages = {
    validFromDate: LanguageUtils.createMessage('Valid from'),
    validToDate: LanguageUtils.createMessage('Valid to'),
    customer: LanguageUtils.createMessage('Customer'),
    priorityRank: LanguageUtils.createMessage('Priority rank'),
    cancellationRuleSet: LanguageUtils.createMessage('Cancellation rule set')
};

const CustomerCancellationRuleSetList = forwardRef((props: CustomerCancellationRuleSetListProps, ref: any): JSX.Element => {
    const { componentsProps, isLoading, checkSelection, onPageChange, rows, hiddenColumns, multipleSelection, onSortModelChange,
        columnVisibilityModel, onColumnVisibilityModelChange, setCurrentComponentId, isFromCancellationRuleSet } = props;

    const defaultFn = (cellValues: GridRenderCellParams | GridValueGetterParams) => cellValues.row;

    const applyRowFn = props.applyRowFn || defaultFn;

    const dataGridRef = useRef();

    useImperativeHandle(ref, () => ({
        resetDataGridPage() {
            if(dataGridRef !== null && dataGridRef !== undefined && dataGridRef.current !== null && dataGridRef.current !== undefined) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                dataGridRef.current.resetPage();
            }
        }
    }));

    useEffect(() => {
        if(setCurrentComponentId !== undefined) {
            setCurrentComponentId(HelptextConstantsEnum.REPORT_KICKBACK_POSTEN);
        }
    }, []);

    function generateColumns(): GridColDef[] {
        const intl = useIntl();
        let x;

        if(isFromCancellationRuleSet) {
            x = [
                {
                    field: 'customer',
                    headerName: intl.formatMessage(messages.customer),
                    flex: 1,
                    align: 'left',
                    headerAlign: 'left',
                    sortable: true,
                    renderCell: (cellValues: GridRenderCellParams) => {
                        return <CustomStringLink value={applyRowFn(cellValues)?.customer?.name}
                            link={`/#/${UrlConstants.CUSTOMERS}/${applyRowFn(cellValues)?.customer?.id}`} />;
                    },
                    hide: hiddenColumns && TableUtils.hideColumn('customer', hiddenColumns)
                }];
        }
        else {
            x = [
                {
                    field: 'cancellationRuleSet',
                    headerName: intl.formatMessage(messages.cancellationRuleSet),
                    flex: 1.5,
                    align: 'left',
                    headerAlign: 'left',
                    sortable: true,
                    renderCell: (cellValues: GridRenderCellParams) => {
                        return <CustomStringLink disabled={true} value={applyRowFn(cellValues)?.cancellationRuleSet?.name}
                            link={`/#/${UrlConstants.CANCELLATION_RULE_SET}/${applyRowFn(cellValues)?.cancellationRuleSet?.id}`} />;
                    },
                    hide: hiddenColumns && TableUtils.hideColumn('cancellationRuleSet', hiddenColumns)
                }];
        }

        x = [
            ...x,
            {
                field: 'validFromDate',
                headerName: intl.formatMessage(messages.validFromDate),
                flex: 1,
                sortable: true,
                renderCell: (params: GridRenderCellParams) => {
                    return <NullableFormattedDate value={applyRowFn(params).validFromDate} />;
                },
                hide: hiddenColumns && TableUtils.hideColumn('validFromDate', hiddenColumns)
            },
            {
                field: 'validToDate',
                headerName: intl.formatMessage(messages.validToDate),
                flex: 1,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (params: GridRenderCellParams) => {
                    return <NullableFormattedDate value={applyRowFn(params).validToDate}/>;
                },
                hide: hiddenColumns && TableUtils.hideColumn('validToDate', hiddenColumns)
            },
            {
                field: 'priorityRank',
                headerName: intl.formatMessage(messages.priorityRank),
                flex: 1,
                align: 'left',
                headerAlign: 'left',
                sortable: true,
                renderCell: (cellValues: GridRenderCellParams) => {
                    return <TruncatedStringTooltip value={applyRowFn(cellValues)?.priorityRank}/>;
                },
                hide: hiddenColumns && TableUtils.hideColumn('priorityRank', hiddenColumns)
            }

        ] as GridColDef[];

        return x;
    }

    return (
        <DataGridComponent rows={rows} multipleSelection={multipleSelection} columns={generateColumns()}
            ref={dataGridRef} componentsProps={componentsProps} isLoading={isLoading}
            checkSelection={checkSelection} onPageChange={onPageChange} onSortModelChange={onSortModelChange}
            columnVisibilityModel={columnVisibilityModel} onColumnVisibilityModelChange={onColumnVisibilityModelChange} />
    );
});

export default CustomerCancellationRuleSetList;
